import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  CircularProgress,
  Snackbar,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Add,
  Delete,
  Star,
  ArrowUpward,
  ArrowDownward,
  OpenInNew,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDevice } from '../../contexts/DeviceContext';
import { FavoriteUrl } from '../../services/api';
import BrowserSettingsSection from '../BrowserSettingsSection';
import { displayHost, displayUrl, normalizeUrl } from '../../utils/url';

/** The confirm button ignores taps this long after the dialog opens or shows an error (BA-55). */
export const TAP_GUARD_MS = 500;
/** Saving stops waiting for the write after this long, then re-reads the list. */
export const SAVE_TIMEOUT_MS = 15000;
/** The re-read after a failed or timed-out write stops waiting after this long. */
export const REREAD_TIMEOUT_MS = 5000;

const TIMEOUT = Symbol('timeout');

/** Resolves to the promise's value, or TIMEOUT when it rejects or takes longer than ms. */
function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | typeof TIMEOUT> {
  return new Promise(resolve => {
    const timer = setTimeout(() => resolve(TIMEOUT), ms);
    promise.then(
      value => { clearTimeout(timer); resolve(value); },
      () => { clearTimeout(timer); resolve(TIMEOUT); },
    );
  });
}

const SITE_SLOT = '\u0001';
const DEVICE_SLOT = '\u0002';

/**
 * A confirm message with its <b>…</b> parts in bold. The site and device name are put in
 * after the markup is split, so a value can never be read as markup and React escapes it.
 */
function boldMessage(template: string, site: string, device: string): React.ReactNode[] {
  const fill = (text: string) => text.split(SITE_SLOT).join(site).split(DEVICE_SLOT).join(device);
  return template.split(/<b>(.*?)<\/b>/).map((part, i) => (i % 2 === 1
    ? <Box key={i} component="b" sx={{ color: 'text.primary', overflowWrap: 'anywhere' }}>{fill(part)}</Box>
    : <React.Fragment key={i}>{fill(part)}</React.Fragment>));
}

/** A Blocked URL or Favourite whose bin was tapped: nothing is written until the parent confirms. */
type PendingRemoval =
  | { kind: 'blocked'; url: string }
  | { kind: 'favorite'; url: string; favorite: FavoriteUrl; index: number };

/** open → saving → closed (written) | error (re-read: still there) | unknown (cannot tell) */
type RemovalPhase = 'open' | 'saving' | 'error' | 'unknown';

type Notice = {
  open: boolean;
  message: string;
  severity: 'success' | 'error';
  duration: number;
  undo?: { deviceId: string; favorite: FavoriteUrl; index: number };
};

const WebsitesPage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    selectedDevice,
    handleAddBlockedUrl,
    handleRemoveBlockedUrl,
    handleAddFavoriteUrl,
    handleRemoveFavoriteUrl,
    handleRestoreFavoriteUrl,
    reloadUrlList,
    handleMoveFavoriteUp,
    handleMoveFavoriteDown,
  } = useDevice();

  const [openDialog, setOpenDialog] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const [newFavoriteUrl, setNewFavoriteUrl] = useState('');

  const [pending, setPending] = useState<PendingRemoval | null>(null);
  const [phase, setPhase] = useState<RemovalPhase>('open');
  const [notice, setNotice] = useState<Notice | null>(null);
  // What the closing dialog keeps showing during its fade-out.
  const lastPending = useRef<PendingRemoval | null>(null);
  if (pending) lastPending.current = pending;
  // Tap guard: the confirm button acts only on a press that began after guardEndsAt.
  const guardEndsAt = useRef(0);
  const pressArmed = useRef(false);
  // A removal or Undo whose id is no longer current (device switch, unmount) reports nothing.
  const requestId = useRef(0);
  // Cancel is the default: focused when the dialog opens and when an error shows.
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (phase === 'error' || phase === 'unknown') cancelRef.current?.focus();
  }, [phase]);

  const deviceId = selectedDevice?.id;
  useEffect(() => {
    requestId.current += 1;
    setPending(null);
    setNotice(null);
  }, [deviceId]);

  useEffect(() => () => { requestId.current += 1; }, []);

  if (!selectedDevice) return null;

  const handleAddBlockedUrlSubmit = async () => {
    await handleAddBlockedUrl(newUrl);
    setNewUrl('');
    setOpenDialog('');
  };

  const handleAddFavoriteUrlSubmit = async () => {
    await handleAddFavoriteUrl(newFavoriteUrl);
    setNewFavoriteUrl('');
    setOpenDialog('');
  };

  const armGuard = () => {
    guardEndsAt.current = performance.now() + TAP_GUARD_MS;
    pressArmed.current = false;
  };

  // The bin only asks: it never writes.
  const askToRemove = (removal: PendingRemoval) => {
    armGuard();
    setPhase('open');
    setPending(removal);
  };

  const closeRemoval = () => {
    if (phase === 'saving') return;
    setPending(null);
  };

  const confirmRemoval = async (removal: PendingRemoval) => {
    const id = ++requestId.current;
    const devId = selectedDevice.id;
    const { kind, url } = removal;
    setPhase('saving');

    const write = kind === 'blocked'
      ? handleRemoveBlockedUrl(devId, url)
      : handleRemoveFavoriteUrl(devId, url);
    const written = await withTimeout(write, SAVE_TIMEOUT_MS);
    if (id !== requestId.current) return;

    let landed = written === true;
    let stillThere = false;
    if (!landed) {
      // An error response or no answer: re-read the list before saying anything about it.
      const list = await withTimeout(reloadUrlList(devId, kind), REREAD_TIMEOUT_MS);
      if (id !== requestId.current) return;
      if (Array.isArray(list)) {
        landed = !list.includes(url);
        stillThere = !landed;
      }
    }

    if (landed) {
      const site = displayUrl(url);
      setPending(null);
      setNotice(removal.kind === 'blocked'
        ? { open: true, message: t('urls.urlUnblockedSuccess'), severity: 'success', duration: 4000 }
        : {
            open: true,
            message: t('urls.favoriteRemoved', { site }),
            severity: 'success',
            duration: 6000,
            undo: { deviceId: devId, favorite: removal.favorite, index: removal.index },
          });
      return;
    }
    // "Still blocked" only when an error response was re-read as unchanged. After a
    // timeout the write may still land, so the dialog claims nothing.
    armGuard();
    setPhase(stillThere && written === false ? 'error' : 'unknown');
  };

  const onConfirmPointerDown = () => {
    pressArmed.current = performance.now() >= guardEndsAt.current;
  };

  const onConfirmClick = (event: React.MouseEvent) => {
    // A keyboard activation (detail 0) has no press: it only waits for the guard.
    const ready = event.detail === 0
      ? performance.now() >= guardEndsAt.current
      : pressArmed.current;
    pressArmed.current = false;
    if (ready && pending && phase !== 'saving') confirmRemoval(pending);
  };

  const undoFavoriteRemoval = async () => {
    const undo = notice?.undo;
    if (!undo) return;
    const id = requestId.current;
    const site = displayUrl(undo.favorite.url);
    setNotice(null);
    const ok = await handleRestoreFavoriteUrl(undo.deviceId, undo.favorite, undo.index);
    if (id !== requestId.current) return;
    setNotice(ok
      ? { open: true, message: t('urls.favoriteRestored', { site }), severity: 'success', duration: 4000 }
      : { open: true, message: t('urls.favoriteRestoreFailed', { site }), severity: 'error', duration: 6000 });
  };

  const closeNotice = (_event: unknown, reason?: string) => {
    if (reason === 'clickaway') return;
    setNotice(prev => prev && { ...prev, open: false });
  };

  const shown = pending ?? lastPending.current;
  const copy = shown?.kind === 'favorite' ? 'urls.removeFavoriteConfirm' : 'urls.unblockConfirm';
  const shownSite = shown ? displayUrl(shown.url) : '';

  return (
    <>
      {/* What the child can do in the tablet's Browser (BA-21) */}
      <BrowserSettingsSection />

      {/* Favorite URLs */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{t('urls.favoriteUrls')}</Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setOpenDialog('addFavorite')}
            >
              {t('urls.addFavorite')}
            </Button>
          </Box>
          <List>
            {selectedDevice.favorite_urls?.length > 0 ? (
              [...selectedDevice.favorite_urls]
                .sort((a, b) => a.order - b.order)
                .map((favorite, index) => (
                  <ListItem
                    key={index}
                    sx={{
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      pr: 17,
                    }}
                  >
                    <Star sx={{ mr: 1, color: 'primary.main' }} />
                    <ListItemText
                      primary={
                        <Link
                          href={normalizeUrl(favorite.url)}
                          title={favorite.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ wordBreak: 'break-all' }}
                        >
                          {displayUrl(favorite.url)}
                          <OpenInNew sx={{ ml: 0.5, fontSize: 16, verticalAlign: 'middle' }} />
                        </Link>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveFavoriteUp(selectedDevice.id, favorite.url)}
                        disabled={index === 0}
                      >
                        <ArrowUpward />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleMoveFavoriteDown(selectedDevice.id, favorite.url)}
                        disabled={index === selectedDevice.favorite_urls.length - 1}
                      >
                        <ArrowDownward />
                      </IconButton>
                      <IconButton
                        edge="end"
                        size="large"
                        sx={{ ml: 1 }}
                        aria-label={t('urls.removeFavoriteAria', { site: displayUrl(favorite.url) })}
                        onClick={() => askToRemove({ kind: 'favorite', url: favorite.url, favorite, index })}
                      >
                        <Delete />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))
            ) : (
              <Typography color="textSecondary">{t('urls.noFavoriteUrls')}</Typography>
            )}
          </List>
        </CardContent>
      </Card>

      {/* Blocked URLs */}
      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">{t('urls.blockedUrls')}</Typography>
            <Button
              variant="outlined"
              startIcon={<Add />}
              onClick={() => setOpenDialog('addUrl')}
            >
              {t('urls.addUrl')}
            </Button>
          </Box>
          <List>
            {selectedDevice.blocked_urls?.length > 0 ? selectedDevice.blocked_urls.map((url, index) => (
              <ListItem key={index} sx={{ pr: 8 }}>
                <ListItemText
                  primary={
                    <Link
                      href={normalizeUrl(url)}
                      title={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ wordBreak: 'break-all' }}
                    >
                      {displayUrl(url)}
                      <OpenInNew sx={{ ml: 0.5, fontSize: 16, verticalAlign: 'middle' }} />
                    </Link>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="large"
                    aria-label={t('urls.unblockAria', { site: displayUrl(url) })}
                    onClick={() => askToRemove({ kind: 'blocked', url })}
                  >
                    <Delete />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            )) : <Typography color="textSecondary">{t('urls.noBlockedUrls')}</Typography>}
          </List>
        </CardContent>
      </Card>

      {/* Unblock / remove-Favourite confirm (BA-55): the only place a removal is written */}
      <Dialog
        open={!!pending}
        onClose={closeRemoval}
        maxWidth="xs"
        fullWidth
        aria-labelledby="remove-url-title"
        // autoFocus alone loses to the dialog's focus trap in a browser.
        slotProps={{ transition: { onEntering: () => cancelRef.current?.focus() } }}
      >
        <DialogTitle id="remove-url-title">{t(`${copy}.title`)}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {boldMessage(
              t(`${copy}.message`, { site: SITE_SLOT, device: DEVICE_SLOT }),
              shownSite,
              selectedDevice.name,
            )}
          </DialogContentText>
          {shown && shownSite !== shown.url && (
            <DialogContentText variant="body2" sx={{ mt: 1, wordBreak: 'break-all' }}>
              {t('urls.storedAs', { stored: shown.url })}
            </DialogContentText>
          )}
          {(phase === 'error' || phase === 'unknown') && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t(`${copy}.${phase}`, { site: shownSite })}
            </Alert>
          )}
        </DialogContent>
        <DialogActions
          disableSpacing
          sx={isPhone
            ? { flexDirection: 'column', alignItems: 'stretch', gap: '12px', p: '8px 24px 24px' }
            : { gap: 1, px: 3, pb: 2 }}
        >
          {/* Destructive first; Cancel is rightmost on desktop and at the bottom on a phone. */}
          <Button
            variant="outlined"
            color="error"
            disabled={phase === 'saving'}
            onPointerDown={onConfirmPointerDown}
            onClick={onConfirmClick}
            startIcon={phase === 'saving' ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              textTransform: 'none',
              minWidth: 0,
              ...(isPhone
                ? { minHeight: 48, alignSelf: 'flex-start', maxWidth: 'calc(100% - 32px)' }
                : { minHeight: 40, maxWidth: 260 }),
            }}
          >
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {phase === 'saving'
                ? t(`${copy}.saving`)
                : t(`${copy}.confirm`, { host: shown ? displayHost(shown.url) : '' })}
            </Box>
          </Button>
          <Button
            ref={cancelRef}
            variant="contained"
            autoFocus
            disabled={phase === 'saving'}
            onClick={closeRemoval}
            sx={{
              textTransform: 'none',
              ...(isPhone ? { minHeight: 48 } : { minHeight: 40, minWidth: 96 }),
            }}
          >
            {t('common.cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Result of a confirmed removal; a removed Favourite can be put back for 6 s */}
      <Snackbar
        open={!!notice?.open}
        autoHideDuration={notice?.duration}
        onClose={closeNotice}
      >
        <Alert
          severity={notice?.severity ?? 'success'}
          action={notice?.undo && (
            <Button color="inherit" onClick={undoFavoriteRemoval} sx={{ minWidth: 64, minHeight: 44, textTransform: 'none' }}>
              {t('urls.undo')}
            </Button>
          )}
        >
          {notice?.message}
        </Alert>
      </Snackbar>

      {/* Add Blocked URL Dialog */}
      <Dialog open={openDialog === 'addUrl'} onClose={() => setOpenDialog('')}>
        <DialogTitle>{t('urls.addBlockedUrlDialog')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('urls.urlToBlock')}
            fullWidth
            variant="outlined"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.cancel')}</Button>
          <Button onClick={handleAddBlockedUrlSubmit} variant="contained">{t('common.add')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Favorite URL Dialog */}
      <Dialog open={openDialog === 'addFavorite'} onClose={() => setOpenDialog('')}>
        <DialogTitle>{t('urls.addFavoriteUrlDialog')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t('urls.urlToFavorite')}
            fullWidth
            variant="outlined"
            value={newFavoriteUrl}
            onChange={(e) => setNewFavoriteUrl(e.target.value)}
            placeholder="https://example.com"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog('')}>{t('common.cancel')}</Button>
          <Button onClick={handleAddFavoriteUrlSubmit} variant="contained" startIcon={<Star />}>
            {t('urls.addToFavorites')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WebsitesPage;
